import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { CheckCircle2, Cpu, Layers, Loader2 } from 'lucide-react'
import { router } from '@inertiajs/react'
import type { Project } from '@/types/conversations'

interface LiveScaleSectionProps {
  project: Project
}

const COMPUTE_TIERS = ['hobby', 'pro', 'startup', 'business'] as const

export function LiveScaleSection({ project }: LiveScaleSectionProps) {
  const { t } = useTranslation('conversations')
  const [pendingTier, setPendingTier] = useState<string | null>(null)
  const [updating, setUpdating] = useState(false)
  const currentComputeTier = project.compute_tier || 'hobby'
  const currentDeployType = project.deploy_tier
  const pricing = project.compute_tier_pricing

  function handleTierConfirm() {
    if (!pendingTier || updating) return

    setUpdating(true)
    router.patch(`/projects/${project.id}/compute_tier`, {
      compute_tier: pendingTier,
    }, {
      preserveScroll: true,
      onFinish: () => {
        setUpdating(false)
        setPendingTier(null)
      },
    })
  }

  const pendingCost = pendingTier && currentDeployType && pricing?.[pendingTier]
    ? pricing[pendingTier][currentDeployType]
    : null

  return (
    <div className="p-4 space-y-5">
      {/* Deploy type (auto-detected, read-only) */}
      <div>
        <div className="flex items-center gap-2 mb-1.5">
          <Layers className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium text-muted-foreground">
            {t('live.sre.scale.deployType')}
          </span>
        </div>
        <Badge variant="secondary" className="text-xs">
          {currentDeployType
            ? t(`live.sre.scale.deployTypes.${currentDeployType}`)
            : t('live.sre.scale.deployTypes.unknown')}
        </Badge>
      </div>

      {/* Compute tier selector */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Cpu className="h-4 w-4 text-muted-foreground" />
          <h3 className="font-semibold text-sm">{t('live.sre.scale.title')}</h3>
        </div>

        <div className="space-y-2">
          {COMPUTE_TIERS.map((tier) => {
            const isCurrent = currentComputeTier === tier
            const cost = currentDeployType && pricing?.[tier]
              ? pricing[tier][currentDeployType]
              : null

            return (
              <button
                key={tier}
                type="button"
                disabled={isCurrent || updating}
                onClick={() => setPendingTier(tier)}
                className={`w-full flex items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
                  isCurrent
                    ? 'border-primary bg-primary/5'
                    : updating
                      ? 'border-border opacity-40 cursor-not-allowed'
                      : 'border-border opacity-60 hover:opacity-100 hover:border-primary/50 cursor-pointer'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">
                      {t(`live.sre.scale.computeTiers.${tier}`)}
                    </span>
                    {isCurrent && (
                      <Badge variant="default" className="text-[10px] px-1.5 py-0">
                        <CheckCircle2 className="h-3 w-3 mr-0.5" />
                        {t('live.sre.scale.currentTier')}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t(`live.sre.scale.computeTierDesc.${tier}`)}
                  </p>
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  {cost === 0
                    ? t('live.sre.scale.free')
                    : cost != null
                      ? t('live.sre.scale.creditsPerMonth', { cost })
                      : '—'}
                </span>
              </button>
            )
          })}

          {/* Enterprise row */}
          <div className="flex items-center gap-3 rounded-lg border border-border opacity-60 p-3">
            <div className="flex-1 min-w-0">
              <span className="font-medium text-sm">
                {t('live.sre.scale.computeTiers.enterprise')}
              </span>
              <p className="text-xs text-muted-foreground">
                {t('live.sre.scale.computeTierDesc.enterprise')}
              </p>
            </div>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {t('live.sre.scale.contactSales')}
            </span>
          </div>
        </div>
      </div>

      {/* Confirmation dialog */}
      <AlertDialog open={!!pendingTier} onOpenChange={(open) => !open && setPendingTier(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('live.sre.scale.confirmTitle', {
                tier: pendingTier ? t(`live.sre.scale.computeTiers.${pendingTier}`) : '',
              })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingTier && t('live.sre.scale.confirmDesc', {
                resources: t(`live.sre.scale.computeTierDesc.${pendingTier}`),
                cost: pendingCost === 0
                  ? t('live.sre.scale.free')
                  : pendingCost != null
                    ? t('live.sre.scale.creditsPerMonth', { cost: pendingCost })
                    : '—',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updating}>
              {t('live.sre.scale.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleTierConfirm} disabled={updating}>
              {updating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('live.sre.scale.confirmAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
